import { useAuth } from '@/lib/auth/AuthProvider';
import { useMemo } from 'react';
import type { LabAcquisitionOffer } from './labAcquisitionHooks';
/**
 * Patient-portal hooks for the post-purchase upsell experiment.
 *
 * Backed by /me/recent-purchase, /upsell/quote, /upsell/confirm — see
 * backend/app/api/v1/upsell.py. The "should this overlay open right
 * now?" decision combines these queries with the cohort cookie set on
 * systemlabs.com (read via `lib/experiments.ts`).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from './client';

export interface RecentPurchase {
  source_product_id: string;
  source_sanity_id: string;
  source_product_title: string;
  purchased_at: string;
  has_upsells: boolean;
  hero_image_url: string | null;
}

export interface UpsellQuoteItem {
  sanity_product_id: string;
  canonical_product_id: string | null;
  slug: string | null;
  title: string;
  description: string | null;
  treatment_support: string | null;
  method: string | null;
  quantity: string | null;
  pricing_mode: string | null;
  monthly_price_cents: number | null;
  monthly_compare_price_cents: number | null;
  monthly_renewal_price_cents: number | null;
  quarterly_price_cents: number | null;
  quarterly_compare_price_cents: number | null;
  quarterly_renewal_price_cents: number | null;
  hero_image_url: string | null;
  value_props: string[];
}

export interface UpsellQuote {
  source_sanity_id: string;
  source_product_title: string;
  items: UpsellQuoteItem[];
}

export interface UpsellConfirmItem {
  cart_promotion_line_key?: string;
  sanity_product_id: string;
  frequency: 'monthly' | 'quarterly';
}

export interface UpsellConfirmIn {
  cart_promotion_id?: string;
  purchase_request_id?: string;
  payment_method_id?: string;
  /** Fresh token from an Apple Pay sheet the patient just approved. */
  wallet_payment_method_id?: string;
  source_sanity_id: string;
  items: UpsellConfirmItem[];
  experiment_key?: string;
  variant_key?: string;
  // Optional Stripe promotion code — backend re-validates and applies it
  // to the created subscription(s).
  promo_code?: string;
}

export interface UpsellConfirmResultItem {
  sanity_product_id: string;
  ok: boolean;
  pharmacy_order_id: string | null;
  subscription_id: string | null;
  canonical_product_id: string | null;
  intake_required: boolean;
  intake_form_id: string | null;
  // When `ok=false`, why. "already_subscribed" is treated as a soft
  // success by the overlay (patient effectively has this product
  // already) and the drawer closes cleanly.
  error_code: string | null;
  error_message: string | null;
  // A declined charge: the bank's reason and the card that failed.
  decline_code?: string | null;
  failed_payment_method_id?: string | null;
  // What the first invoice charged (exact revenue); absent on old backends.
  amount_paid_cents?: number | null;
}

export interface UpsellConfirmOut {
  items: UpsellConfirmResultItem[];
  /** "single": every first month was one card charge, so a decline fails
   *  every item together. Absent from older backends (= per item). */
  charge_mode?: 'single' | 'per_item';
}

/**
 * Recent purchase within the upsell eligibility window (24h). Returns
 * `null` (not an error) when there's nothing recent — TanStack Query
 * caches the null so we don't poll the backend on every render.
 */
export function useRecentPurchase(opts: { enabled?: boolean } = {}) {
  return useQuery<RecentPurchase | null>({
    queryKey: ['me', 'recent-purchase'],
    queryFn: () => apiFetch<RecentPurchase | null>('/me/recent-purchase'),
    staleTime: 5 * 60_000,
    enabled: opts.enabled ?? true,
    // The Stripe payment webhook provisions the pharmacy_orders row this
    // endpoint reads, and fast claimers reach /onboarding/welcome 1-5
    // seconds BEFORE that row lands (measured 2026-07-07: 20/20 missed
    // post-purchase drawers had their order row created 1-5s after the
    // welcome pageview). A null first response therefore usually means
    // "not provisioned yet", not "no purchase" — poll every 4s (~40s
    // total) before accepting the null as final.
    refetchInterval: (query) =>
      query.state.data == null && query.state.dataUpdateCount < 10
        ? 4_000
        : false,
  });
}

export interface PlanUpsell {
  subscription_id: string;
  product_id: string;
  product_title: string;
  monthly_renewal_cents: number;
  quarterly_renewal_cents: number;
  quarterly_per_month_cents: number;
  savings_per_month_cents: number;
  savings_per_quarter_cents: number;
  // null → hide the "vials up front" benefit line (quarterly ships 1-1-1
  // for this product because of BUD/max-supply caps).
  first_shipment_containers: number | null;
  container_noun: string | null;
  next_renewal_date: string | null;
  paid_through?: number | null;
  items?: PlanUpsell[];
}

export interface ConsultUpsell {
  title: string;
  price_cents: number;
  // One-time purchase — deliberately no subscription (CEO framing:
  // "drop the ongoing monthly/quarterly relationship").
  one_time: true;
  // Fake-door demand test: accept fakes a beat of processing then shows
  // "not available in your state / you weren't charged" — no charge, no
  // endpoint call. Flips off server-side (PEPTIDE_EVALUATION_FAKEDOOR)
  // once the ops workflow exists.
  fakedoor?: boolean;
}

export interface PostPurchaseOffer {
  // Eligibility and assignment are resolved by the backend; absent outside A.
  lab_upsell?: LabAcquisitionOffer | null;
  purchase: RecentPurchase | null;
  quote: UpsellQuote | null;
  // Non-null when the purchase qualifies for the quarterly plan-upsell
  // step (monthly gluta/NAD/serm purchase with quarterly pricing).
  // Cohort gating happens client-side via `quarterly-renewal-upsell-v1`.
  plan_upsell?: PlanUpsell | null;
  bundle_plan_upsell?: PlanUpsell | null;
  plan_upsell_pending?: boolean;
  // Non-null when the patient could take the provider-consult membership
  // (card on file, not already a member). Cohort gating happens
  // client-side via `consult-upsell-v1`.
  consult_upsell?: ConsultUpsell | null;
}

/**
 * One-call replacement for useRecentPurchase + useUpsellQuote — the two
 * are inherently serial (the quote needs the purchase's source id), so
 * the combined endpoint halves the drawer's time-to-open. Carries the
 * same brief while-null poll as useRecentPurchase: webhook provisioning
 * can trail the claim redirect by a few seconds, and a null `purchase`
 * in that window means "not provisioned yet", not "no purchase".
 */
export function usePostPurchaseOffer(opts: { enabled?: boolean; refetchOnMount?: boolean | 'always'; waitForLabOffer?: boolean } = {}) {
  const { session } = useAuth();
  const owner = session?.user.id;
  const labBindingDeadline = useMemo(
    () => Date.now() + 40_000,
    [owner, opts.waitForLabOffer],
  );
  return useQuery<PostPurchaseOffer>({
    queryKey: ['me', 'post-purchase-offer', owner],
    queryFn: () => apiFetch<PostPurchaseOffer>('/me/post-purchase-offer'),
    staleTime: 5 * 60_000,
    enabled: Boolean(owner) && (opts.enabled ?? true),
    refetchOnMount: opts.refetchOnMount ?? true,
    // Keep the accepted quote stable during a multi-subscription mutation.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: (query) => {
      const polls = query.state.dataUpdateCount + query.state.errorUpdateCount;
      // While the purchase or its stack is still being set up, poll every 2s
      // (same 40s / 120s budgets as before): the welcome screen and the
      // popup are both waiting on this answer.
      if (query.state.data?.purchase == null) {
        return polls < 20 ? 2_000 : false;
      }
      // Bundle legs provision after the anchor; wait for all of them rather
      // than open a one-product offer.
      if (query.state.data.plan_upsell_pending) return polls < 60 ? 2_000 : false;
      // Paid order creation can precede its optional lab-offer binding. The
      // existing post-purchase list gets a short retry window; normal
      // visits and already-available offers never run a persistent poll.
      const age = Date.now() - Date.parse(query.state.data.purchase.purchased_at);
      return opts.waitForLabOffer && !query.state.data.lab_upsell &&
        age >= 0 && age < 5 * 60_000 && Date.now() < labBindingDeadline
        ? 4_000 : false;
    },
  });
}

export function useUpsellQuote(
  sourceSanityId: string | null | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<UpsellQuote>({
    queryKey: ['upsell', 'quote', sourceSanityId],
    queryFn: () =>
      apiFetch<UpsellQuote>(
        `/upsell/quote?source_sanity_id=${encodeURIComponent(
          sourceSanityId as string,
        )}`,
      ),
    enabled: Boolean(sourceSanityId) && (opts.enabled ?? true),
    staleTime: 10 * 60_000,
  });
}

export interface PeptideEvaluationResult {
  payment_intent_id: string;
  price_cents: number;
  already_purchased: boolean;
  tag_applied: boolean;
}

/** One-click, ONE-TIME peptide-evaluation purchase on the saved card —
 *  the accept action of the `consult-upsell-v1` drawer step. Idempotent
 *  server-side (a prior purchase returns already_purchased instead of
 *  double-charging). */
export function usePurchasePeptideEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      source?: string;
      experiment_key?: string;
      variant_key?: string;
    }) =>
      apiFetch<PeptideEvaluationResult>('/me/peptide-evaluation', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      // The offer block goes null once purchased, and home grows the
      // 'Schedule your peptide consult' task.
      qc.invalidateQueries({ queryKey: ['me', 'post-purchase-offer'] });
      qc.invalidateQueries({ queryKey: ['home'] });
    },
  });
}

/** Terms the post-purchase quarterly offer was quoted at; the backend
 * rejects the switch (409) if any changed since. */
export interface PlanOfferTerms {
  product_id: string;
  monthly_renewal_cents: number;
  quarterly_renewal_cents: number;
  paid_through: number;
  requested_at: number;
}

/** The post-purchase quarterly switch: the change-plan call with the quoted
 * terms attached. Account → Subscriptions keeps useChangeSubscriptionPlan. */
export function useAcceptQuarterlyPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      subId,
      frequency,
      source,
      experimentKey,
      variantKey,
      expected,
    }: {
      subId: string;
      frequency: 'quarterly';
      source?: string;
      experimentKey?: string;
      variantKey?: string;
      expected?: PlanOfferTerms;
    }) =>
      apiFetch<{ ok?: boolean }>(`/me/subscriptions/${subId}/change-plan`, {
        method: 'POST',
        body: JSON.stringify({
          frequency,
          ...(source ? { source } : {}),
          ...(experimentKey ? { experiment_key: experimentKey } : {}),
          ...(variantKey ? { variant_key: variantKey } : {}),
          ...(expected ? { expected } : {}),
        }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['me', 'renewal-checkins'] });
      qc.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'me' && q.queryKey[1] === 'subscriptions',
      });
    },
  });
}

export function useConfirmUpsell() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsellConfirmIn) =>
      apiFetch<UpsellConfirmOut>('/upsell/confirm', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      // Confirmed upsells create new pharmacy_orders + intake rows;
      // invalidate the obvious dashboards so the patient sees them
      // immediately on /app and /app/orders.
      qc.invalidateQueries({ queryKey: ['me', 'orders'] });
      qc.invalidateQueries({ queryKey: ['home'] });
      qc.invalidateQueries({ queryKey: ['me', 'required-intakes'] });
      qc.invalidateQueries({ queryKey: ['marketplace'] });
    },
  });
}
